import { z } from 'zod';

import { Id, IsoDateTime, Paise } from '../common/primitives';

export { Paise } from '../common/primitives';

// ── Enums ──────────────────────────────────────────────────────────────────

export const BusinessAccountStatusSchema = z.enum(['PENDING_APPROVAL', 'ACTIVE', 'SUSPENDED', 'BLOCKED']);
export type BusinessAccountStatus = z.infer<typeof BusinessAccountStatusSchema>;

export const BusinessAccountUserRoleSchema = z.enum(['OWNER', 'BUYER', 'FINANCE', 'VIEWER']);
export type BusinessAccountUserRole = z.infer<typeof BusinessAccountUserRoleSchema>;

export const B2bContractStatusSchema = z.enum(['DRAFT', 'PENDING_APPROVAL', 'ACTIVE', 'REJECTED', 'EXPIRED']);
export type B2bContractStatus = z.infer<typeof B2bContractStatusSchema>;

export const CreditTermsSchema = z.enum(['PREPAID', 'NET_15', 'NET_30', 'NET_60', 'NET_90']);
export type CreditTerms = z.infer<typeof CreditTermsSchema>;

export const RfqStatusSchema = z.enum([
  'DRAFT',
  'SUBMITTED',
  'QUOTED',
  'ACCEPTED',
  'REJECTED',
  'CONVERTED',
  'EXPIRED',
]);
export type RfqStatus = z.infer<typeof RfqStatusSchema>;

export const B2bOrderStatusSchema = z.enum(['DRAFT', 'PENDING_APPROVAL', 'CONFIRMED', 'FULFILLED', 'CANCELLED']);
export type B2bOrderStatus = z.infer<typeof B2bOrderStatusSchema>;

export const B2bInvoiceStatusSchema = z.enum(['PENDING', 'PARTIALLY_PAID', 'PAID', 'OVERDUE', 'CANCELLED']);
export type B2bInvoiceStatus = z.infer<typeof B2bInvoiceStatusSchema>;

export const B2bPaymentMethodSchema = z.enum([
  'CASH',
  'UPI',
  'BANK_TRANSFER',
  'CHEQUE',
  'RAZORPAY',
  'CREDIT_NOTE',
]);
export type B2bPaymentMethod = z.infer<typeof B2bPaymentMethodSchema>;

// ── Shared validators ──────────────────────────────────────────────────────

const Gstin = z
  .string()
  .trim()
  .regex(/^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/, 'Invalid GSTIN format');
const Pan = z.string().trim().regex(/^[A-Z]{5}\d{4}[A-Z]$/, 'Invalid PAN format');
const StateCode = z.string().trim().length(2);

const AddressSchema = z
  .object({
    line1: z.string().trim().min(2).max(200),
    line2: z.string().trim().max(200).optional(),
    city: z.string().trim().min(2).max(100),
    stateCode: StateCode,
    pincode: z.string().trim().regex(/^\d{6}$/),
  })
  .strict();

// ── Business account ───────────────────────────────────────────────────────

export const BusinessAccountSchema = z.object({
  id: Id,
  code: z.string(),
  name: z.string(),
  legalName: z.string().nullable(),
  gstin: z.string().nullable(),
  panNumber: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  stateCode: z.string(),
  creditLimitPaise: Paise,
  outstandingPaise: Paise,
  defaultCreditTerms: CreditTermsSchema,
  status: BusinessAccountStatusSchema,
  gstCertificateUrl: z.string().nullable().optional(),
  panCardUrl: z.string().nullable().optional(),
  creditApplicationUrl: z.string().nullable().optional(),
  creditApprovedById: Id.nullable().optional(),
  creditApprovedAt: IsoDateTime.nullable().optional(),
  salesRepId: Id.nullable(),
  approvedAt: IsoDateTime.nullable(),
  notes: z.string().nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type BusinessAccount = z.infer<typeof BusinessAccountSchema>;

export const CreateBusinessAccountRequest = z
  .object({
    code: z.string().trim().regex(/^[A-Z0-9_-]{2,30}$/, 'Code must be uppercase alphanumeric'),
    name: z.string().trim().min(2).max(200),
    legalName: z.string().trim().max(200).optional(),
    gstin: Gstin.optional(),
    panNumber: Pan.optional(),
    email: z.string().trim().email().optional(),
    phone: z.string().trim().max(20).optional(),
    billingAddress: AddressSchema,
    shippingAddress: AddressSchema.optional(),
    stateCode: StateCode,
    creditLimitPaise: Paise.optional(),
    defaultCreditTerms: CreditTermsSchema.optional(),
    gstCertificateUrl: z.string().url().optional(),
    panCardUrl: z.string().url().optional(),
    creditApplicationUrl: z.string().url().optional(),
    salesRepId: Id.optional(),
    notes: z.string().trim().max(2000).optional(),
  })
  .strict();
export type CreateBusinessAccountRequest = z.infer<typeof CreateBusinessAccountRequest>;

export const UpdateBusinessAccountRequest = CreateBusinessAccountRequest.partial().extend({
  status: BusinessAccountStatusSchema.optional(),
});
export type UpdateBusinessAccountRequest = z.infer<typeof UpdateBusinessAccountRequest>;

export const ReviewBusinessAccountRequest = z
  .object({
    creditLimitPaise: Paise,
    creditTerms: CreditTermsSchema,
    status: BusinessAccountStatusSchema.default('ACTIVE'),
  })
  .strict();
export type ReviewBusinessAccountRequest = z.infer<typeof ReviewBusinessAccountRequest>;

export const AddBusinessAccountUserRequest = z
  .object({
    userId: Id,
    role: BusinessAccountUserRoleSchema.default('BUYER'),
  })
  .strict();
export type AddBusinessAccountUserRequest = z.infer<typeof AddBusinessAccountUserRequest>;

export const BusinessAccountUserSchema = z.object({
  id: Id,
  businessAccountId: Id,
  userId: Id,
  role: BusinessAccountUserRoleSchema,
  createdAt: IsoDateTime,
});
export type BusinessAccountUser = z.infer<typeof BusinessAccountUserSchema>;

// ── Sales rep ──────────────────────────────────────────────────────────────

export const SalesRepSchema = z.object({
  id: Id,
  userId: Id,
  code: z.string(),
  commissionBps: z.number().int().nonnegative(),
  active: z.boolean(),
  createdAt: IsoDateTime,
});
export type SalesRep = z.infer<typeof SalesRepSchema>;

export const CreateSalesRepRequest = z
  .object({
    userId: Id,
    code: z.string().trim().regex(/^[A-Z0-9_-]{2,30}$/, 'Code must be uppercase alphanumeric'),
    commissionBps: z.number().int().min(0).max(2000).default(0),
  })
  .strict();
export type CreateSalesRepRequest = z.infer<typeof CreateSalesRepRequest>;

// ── Contract ───────────────────────────────────────────────────────────────

export const B2bContractSchema = z.object({
  id: Id,
  businessAccountId: Id,
  variantId: Id,
  unitPricePaise: Paise,
  minOrderQty: z.number().int(),
  validFrom: IsoDateTime,
  validTo: IsoDateTime.nullable(),
  status: B2bContractStatusSchema,
  approvedById: Id.nullable().optional(),
  approvedAt: IsoDateTime.nullable().optional(),
  rejectedById: Id.nullable().optional(),
  rejectedAt: IsoDateTime.nullable().optional(),
  rejectedReason: z.string().nullable().optional(),
  notes: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type B2bContract = z.infer<typeof B2bContractSchema>;

export const CreateB2bContractRequest = z
  .object({
    businessAccountId: Id,
    variantId: Id,
    unitPricePaise: Paise.refine((v) => v > 0),
    minOrderQty: z.number().int().min(1).default(1),
    validFrom: IsoDateTime.optional(),
    validTo: IsoDateTime.optional(),
    notes: z.string().trim().max(2000).optional(),
  })
  .strict();
export type CreateB2bContractRequest = z.infer<typeof CreateB2bContractRequest>;

export const UpdateB2bContractStatusRequest = z
  .object({
    status: B2bContractStatusSchema,
    rejectedReason: z.string().trim().max(1000).optional(),
  })
  .strict();
export type UpdateB2bContractStatusRequest = z.infer<typeof UpdateB2bContractStatusRequest>;

// ── Price tier (reuses pre-existing B2bPriceTier model) ───────────────────

export const B2bPriceTierSchema = z.object({
  id: Id,
  variantId: Id,
  minQty: z.number().int(),
  pricePaise: Paise,
});
export type B2bPriceTier = z.infer<typeof B2bPriceTierSchema>;

export const UpsertPriceTierRequest = z
  .object({
    variantId: Id,
    minQty: z.number().int().min(1),
    pricePaise: Paise.refine((v) => v > 0),
  })
  .strict();
export type UpsertPriceTierRequest = z.infer<typeof UpsertPriceTierRequest>;

// ── Price quote (read-only price resolution) ──────────────────────────────

export const QuotePriceRequest = z
  .object({
    businessAccountId: Id,
    variantId: Id,
    qty: z.number().int().positive(),
  })
  .strict();
export type QuotePriceRequest = z.infer<typeof QuotePriceRequest>;

export const PriceQuoteSchema = z.object({
  variantId: Id,
  qty: z.number().int(),
  unitPricePaise: Paise,
  /// Which strategy resolved the price.
  source: z.enum(['CONTRACT', 'TIER', 'LIST']),
  contractId: Id.nullable(),
  tierId: Id.nullable(),
});
export type PriceQuote = z.infer<typeof PriceQuoteSchema>;

// ── RFQ ────────────────────────────────────────────────────────────────────

const RfqItemInput = z
  .object({
    variantId: Id,
    description: z.string().trim().min(1).max(300),
    qty: z.number().int().positive(),
    requestedUnitPricePaise: Paise.optional(),
    gstRate: z.number().int().min(0).max(28).default(0),
  })
  .strict();

export const CreateRfqRequest = z
  .object({
    businessAccountId: Id,
    expectedDeliveryAt: IsoDateTime.optional(),
    validUntil: IsoDateTime.optional(),
    notes: z.string().trim().max(2000).optional(),
    items: z.array(RfqItemInput).min(1).max(200),
  })
  .strict();
export type CreateRfqRequest = z.infer<typeof CreateRfqRequest>;

const QuoteRfqLineInput = z
  .object({
    rfqItemId: Id,
    quotedUnitPricePaise: Paise.refine((v) => v > 0),
  })
  .strict();

export const QuoteRfqRequest = z
  .object({
    lines: z.array(QuoteRfqLineInput).min(1).max(200),
    validUntil: IsoDateTime.optional(),
    notes: z.string().trim().max(2000).optional(),
  })
  .strict();
export type QuoteRfqRequest = z.infer<typeof QuoteRfqRequest>;

export const RfqItemSchema = z.object({
  id: Id,
  variantId: Id,
  description: z.string(),
  qty: z.number().int(),
  requestedUnitPricePaise: Paise.nullable(),
  quotedUnitPricePaise: Paise.nullable(),
  gstRate: z.number().int(),
});
export type RfqItem = z.infer<typeof RfqItemSchema>;

export const RfqSchema = z.object({
  id: Id,
  rfqNumber: z.string(),
  businessAccountId: Id,
  requestedById: Id,
  status: RfqStatusSchema,
  quotedById: Id.nullable(),
  expectedDeliveryAt: IsoDateTime.nullable(),
  validUntil: IsoDateTime.nullable(),
  notes: z.string().nullable(),
  quotedAt: IsoDateTime.nullable(),
  acceptedAt: IsoDateTime.nullable(),
  rejectedAt: IsoDateTime.nullable(),
  convertedOrderId: Id.nullable(),
  createdAt: IsoDateTime,
});
export type Rfq = z.infer<typeof RfqSchema>;

export const RfqDetailSchema = RfqSchema.extend({
  items: z.array(RfqItemSchema),
});
export type RfqDetail = z.infer<typeof RfqDetailSchema>;

// ── B2B order ──────────────────────────────────────────────────────────────

export const B2bOrderItemSchema = z.object({
  id: Id,
  variantId: Id,
  description: z.string(),
  sku: z.string().nullable(),
  hsnSac: z.string().nullable(),
  qty: z.number().int(),
  unitPricePaise: Paise,
  gstRate: z.number().int(),
  taxPaise: Paise,
  discountPaise: Paise,
  lineSubtotalPaise: Paise,
  cogsPaise: Paise,
});
export type B2bOrderItem = z.infer<typeof B2bOrderItemSchema>;

export const B2bOrderSchema = z.object({
  id: Id,
  orderNumber: z.string(),
  businessAccountId: Id,
  rfqId: Id.nullable(),
  createdById: Id,
  status: B2bOrderStatusSchema,
  warehouseId: Id.nullable(),
  itemsSubtotalPaise: Paise,
  taxPaise: Paise,
  shippingPaise: Paise,
  discountPaise: Paise,
  grandTotalPaise: Paise,
  cogsPaise: Paise,
  intraState: z.boolean(),
  creditTerms: CreditTermsSchema,
  approvedAt: IsoDateTime.nullable(),
  fulfilledAt: IsoDateTime.nullable(),
  cancelledAt: IsoDateTime.nullable(),
  notes: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type B2bOrder = z.infer<typeof B2bOrderSchema>;

export const B2bOrderDetailSchema = B2bOrderSchema.extend({
  items: z.array(B2bOrderItemSchema),
});
export type B2bOrderDetail = z.infer<typeof B2bOrderDetailSchema>;

const B2bOrderItemInput = z
  .object({
    variantId: Id,
    description: z.string().trim().min(1).max(300),
    sku: z.string().trim().max(64).optional(),
    hsnSac: z.string().trim().max(16).optional(),
    qty: z.number().int().positive(),
    /// If omitted, server resolves via contract → tier → list. Required when manual override.
    unitPricePaise: Paise.optional(),
    gstRate: z.number().int().min(0).max(28).default(0),
    discountPaise: Paise.optional(),
  })
  .strict();

export const CreateB2bOrderRequest = z
  .object({
    businessAccountId: Id,
    rfqId: Id.optional(),
    /// Whichever InternalWarehouse will ship the order. Required at approval; optional at draft.
    warehouseId: Id.optional(),
    creditTerms: CreditTermsSchema.optional(),
    billingAddress: AddressSchema,
    shippingAddress: AddressSchema,
    shippingPaise: Paise.optional(),
    discountPaise: Paise.optional(),
    notes: z.string().trim().max(2000).optional(),
    items: z.array(B2bOrderItemInput).min(1).max(500),
  })
  .strict();
export type CreateB2bOrderRequest = z.infer<typeof CreateB2bOrderRequest>;

export const ApproveB2bOrderRequest = z
  .object({
    warehouseId: Id,
    note: z.string().trim().max(500).optional(),
  })
  .strict();
export type ApproveB2bOrderRequest = z.infer<typeof ApproveB2bOrderRequest>;

// ── Invoice ────────────────────────────────────────────────────────────────

export const B2bInvoiceSchema = z.object({
  id: Id,
  invoiceNumber: z.string(),
  b2bOrderId: Id,
  businessAccountId: Id,
  status: B2bInvoiceStatusSchema,
  subtotalPaise: Paise,
  taxPaise: Paise,
  totalPaise: Paise,
  paidPaise: Paise,
  dueAt: IsoDateTime,
  issuedAt: IsoDateTime,
  paidAt: IsoDateTime.nullable(),
  cancelledAt: IsoDateTime.nullable(),
  notes: z.string().nullable(),
});
export type B2bInvoice = z.infer<typeof B2bInvoiceSchema>;

export const RecordB2bPaymentRequest = z
  .object({
    amountPaise: Paise.refine((v) => v > 0),
    method: B2bPaymentMethodSchema,
    reference: z.string().trim().max(120).optional(),
    notes: z.string().trim().max(500).optional(),
  })
  .strict();
export type RecordB2bPaymentRequest = z.infer<typeof RecordB2bPaymentRequest>;

export const B2bPaymentSchema = z.object({
  id: Id,
  invoiceId: Id,
  amountPaise: Paise,
  method: B2bPaymentMethodSchema,
  reference: z.string().nullable(),
  receivedById: Id,
  receivedAt: IsoDateTime,
  notes: z.string().nullable(),
});
export type B2bPayment = z.infer<typeof B2bPaymentSchema>;
